/**
 * Calculates the total Debit value for a receipt based on its linked FMEs.
 *
 * Debit calculation rules (applied per FME, then summed):
 * 1. If `manual_invoice_total_income` is NOT null on the FME,
 *    use `manual_invoice_total_income`.
 * 2. Else if `invoice_total_income` is NOT null on the FME,
 *    use `invoice_total_income`.
 * 3. Else if `total_income` is NOT null on the FME,
 *    use `total_income`.
 * 4. Otherwise, the FME contributes 0 to Debit.
 *
 * The Debit value for the receipt is the sum of the resolved values
 * from all linked FMEs.
 *
 * Notes:
 * - `null` and `undefined` are treated as "not present".
 * - Zero (0) and negative values are valid and will be included.
 * - Receipt-level totals are NOT used in this calculation.
 *
 * @param linkedFmes - List of FMEs linked to the receipt.
 * @returns The aggregated Debit amount calculated from FME-level values.
 */
export const calculateDebitForReceipt = (linkedFmes: any[]): number => {
  if (!Array.isArray(linkedFmes) || linkedFmes.length === 0) return 0;

  return linkedFmes.reduce((sum, fme) => {
    if (
      fme.manual_invoice_total_income !== null &&
      fme.manual_invoice_total_income !== undefined
    ) {
      return sum + Number(fme.manual_invoice_total_income);
    }

    if (
      fme.invoice_total_income !== null &&
      fme.invoice_total_income !== undefined
    ) {
      return sum + Number(fme.invoice_total_income);
    }

    if (fme.total_income !== null && fme.total_income !== undefined) {
      return sum + Number(fme.total_income);
    }

    return sum;
  }, 0);
};

export const calculateDebitForFme = (fme: any): number => {
  if (!fme) return 0;

  if (
    fme.manual_invoice_total_income !== null &&
    fme.manual_invoice_total_income !== undefined
  ) {
    return Number(fme.manual_invoice_total_income);
  }

  if (
    fme.invoice_total_income !== null &&
    fme.invoice_total_income !== undefined
  ) {
    return Number(fme.invoice_total_income);
  }

  if (fme.total_income !== null && fme.total_income !== undefined) {
    return Number(fme.total_income);
  }

  return 0;
};

/**
 * Calculates the percentage of a receipt amount covered by the Debit value.
 *
 * Formula:
 *   (debit / receiptAmount) * 100
 *
 * The result is rounded to 2 decimal places.
 * If the receipt amount is zero, null, or negative, the function returns 0.
 *
 * @param debit - The calculated Debit amount.
 * @param receiptAmount - The total amount of the receipt.
 * @returns The percentage of the receipt amount covered by the Debit.
 */

export const calculateCoveredPercentage = (
  debit: number,
  receiptAmount: number,
): number => {
  if (!receiptAmount || receiptAmount <= 0) return 0;
  return +((debit / receiptAmount) * 100).toFixed(2);
};

export const calculateCoveredPercentageForFme = (
  fmeDebit: number,
  receiptAmount: number,
): number => {
  if (!receiptAmount || receiptAmount <= 0) return 0;
  return +((fmeDebit / receiptAmount) * 100).toFixed(2);
};

/**
 * Determines whether there is a currency mismatch between a receipt and
 * any of its linked FMEs.
 *
 * Currency comparison rules (evaluated per FME):
 * 1. If `manual_currency_id` exists and is a number on the FME,
 *    compare it with `receipt.receipt_currency_id`.
 * 2. Otherwise, compare the FME `currency` with
 *    `receipt.receipt_currency`.
 *
 * If any single FME fails the comparison, the function returns true.
 * If all FMEs match the receipt currency, or if there are no FMEs,
 * the function returns false.
 *
 * @param receipt - The receipt object containing currency identifiers.
 * @param fmes - List of FMEs linked to the receipt.
 * @returns True if a currency mismatch exists, otherwise false.
 */

export const hasCurrencyMismatch = (receipt: any, fmes: any[]): boolean => {
  if (!receipt || !Array.isArray(fmes) || fmes.length === 0) {
    return false;
  }

  return fmes.some((fme) => {
    if (typeof fme.manual_currency_id === "number") {
      return fme.manual_currency_id !== receipt.receipt_currency_id;
    }
    if (fme.currency && receipt.receipt_currency) {
      return fme.currency !== receipt.receipt_currency;
    }
    return false;
  });
};
